import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MouseEvent as ReactMouseEvent } from 'react';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import ColorLensOutlinedIcon from '@mui/icons-material/ColorLensOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import FitScreenOutlinedIcon from '@mui/icons-material/FitScreenOutlined';
import GroupsIcon from '@mui/icons-material/Groups';
import RefreshIcon from '@mui/icons-material/Refresh';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { Box, CircularProgress, Divider, ListItemIcon, ListItemText, Menu, MenuItem, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { UserNoteGet } from 'api';
import type { DashboardSmallUser } from 'api/models/DashboardSmallUser';
import { ApiService } from 'api/services/ApiService';
import { DESC, VALUATION_DATE_KEY } from 'common/constants/analyst-dashboards';
import { useLocalStorageByUser } from 'common/hooks';
import { LoadingSection } from 'components';
import {
  PrimaryNavigationMenu,
  PrimaryNavigationMenuItem,
  TertiaryNavigation,
  TertiaryNavigationAddButton,
  TertiaryNavigationIconButton,
} from 'components/ui';
import type { TertiaryNavigationItem } from 'components/ui/TertiaryNavigation/TertiaryNavigation.types';
import { EventType } from 'context/EventBusContext/EventBusContext.type';
import { useEventSubscription } from 'context/EventBusContext/useEventSubscription';
import { useZoom } from 'context/ZoomContext';
import {
  DEFAULT_GOOGLE_CALENDAR_SHOW_VACATION_EVENTS,
  GOOGLE_CALENDAR_SHOW_VACATION_EVENTS_KEY,
} from 'dashboard409a/common/constants/calendar';
import { ExpandableSearch, ExpandableSearchHandle } from 'dashboard409a/components/ExpandableSearch';
import ModelsCalendarWithToolbar from 'dashboard409a/components/ModelsCalendar/ModelsCalendarWithToolbar';
import ModelsColorSelector from 'dashboard409a/components/ModelsColorSelector/ModelsColorSelector';
import {
  FilterTypeSelection,
  MODEL_FILTER_TYPES,
  ModelFilterType,
} from 'dashboard409a/components/ModelsTable/ModelsTable.types';
import { OperationsModal, useOperationsModal } from 'dashboard409a/components/OperationsModal';
import {
  calculateTableHeight,
  GenericWorkboard,
  useNoteHandler,
} from 'dashboard409a/components/ValuationsTable/common';
import { ValuationsTableValuationUpdatedPayload } from 'dashboard409a/components/ValuationsTable/ValuationsTable.types';
import ViewToggle, { ViewType } from 'dashboard409a/components/ViewToggle/ViewToggle';
import WorkboardModelsSpreadsheet from 'dashboard409a/components/WorkboardModelsSpreadsheet';
import { useCompanyDashboardContext } from 'dashboard409a/context/CompanyDashboardContext';
import { useColorPalette } from 'dashboard409a/hooks/useColorPalette';
import { useModelsWithValuationData } from 'dashboard409a/hooks/useModelsWithValuationData';
import { useMultiColumnSort } from 'dashboard409a/hooks/useMultiColumnSort';
import { getDefaultModelView, useWorkboardModelsTableViews } from 'dashboard409a/hooks/useWorkboardModelsTableViews';
import { modelsColumns } from 'dashboard409a/pages/Workboard/common/colConfig';
import {
  ACTIVE_MODEL_VIEW_KEY,
  DEFAULT_NEW_VIEW_NAME,
  DEFAULT_VIEW_ID,
  MODELS_COLUMN_IDS,
  MODELS_SORT_KEY,
  MODELS_VIEW_KEY,
} from 'dashboard409a/pages/Workboard/common/constants';
import { resolveModelViewDates } from 'dashboard409a/pages/Workboard/common/trailingDates';
import { CreateNoteRequest } from 'dashboard409a/services/attio.types';
import { RootStateType } from 'dashboard409a/states/store';
import { canEditTableView, type ModelView } from 'dashboard409a/types/workboardTableViews';
import {
  DEFAULT_FILTER_TYPE_SELECTION,
  normalizeFilterTypeSelection,
  normalizeModelFilterType,
} from 'dashboard409a/utilities/modelFilterNormalization';
import { useDebouncedValue } from 'hooks';
import { normalizeDate } from 'utilities';
import ModelsNewViewDialog from './ModelsNewViewDialog';
import { hasViewChanged } from '../common/utils';
import { FulfillmentModelsLeaderboardModal } from '../FulfillmentModelsLeaderboard/FulfillmentModelsLeaderboardModal';
import { GoogleCalendarSettingsModal } from '../GoogleCalendarSettings/GoogleCalendarSettingsModal';
import { UserGroup } from '../UserGroups/UserGroups.types';
import { UserGroupsModal } from '../UserGroups/UserGroupsModal';

interface WorkboardModelsTableProps {
  // Filter type for models
  filterType?: ModelFilterType;
  // Initial preparer user IDs
  preparerUserIds?: number[];
  // Initial reviewer user IDs
  reviewerUserIds?: number[];
}

const WorkboardModelsTable = ({
  filterType: initialFilterType = MODEL_FILTER_TYPES.ASSIGNED,
  preparerUserIds: initialPreparerUserIds,
  reviewerUserIds: initialReviewerUserIds,
}: WorkboardModelsTableProps) => {
  const currentUserId = useSelector((state: RootStateType) => state.user.id);
  const isStaff = useSelector((state: RootStateType) => state.user.is_staff);
  const isSuperuser = useSelector((state: RootStateType) => state.user.is_superuser);

  // Use shared multi-column sort hook
  const {
    sortCriteria,
    orderBy,
    order,
    isReady: isSortReady,
    handleRequestSort,
  } = useMultiColumnSort({
    storageKey: MODELS_SORT_KEY,
    defaultOrderBy: VALUATION_DATE_KEY,
    defaultOrder: DESC,
  });

  // Mount early so cachedCalendarColorPalette is warmed before grid rows render.
  const { isReady: isPaletteReady } = useColorPalette();

  const [filterType, setFilterType] = useState<ModelFilterType>(initialFilterType);
  const [filterTypeSelection, setFilterTypeSelection] = useState<FilterTypeSelection>(DEFAULT_FILTER_TYPE_SELECTION);
  const [preparerIds, setPreparerIds] = useState<number[]>(initialPreparerUserIds ?? []);
  const [reviewerIds, setReviewerIds] = useState<number[]>(initialReviewerUserIds ?? []);
  const searchRef = useRef<ExpandableSearchHandle>(null);
  const [searchParam, setSearchParam] = useState('');
  const debouncedSearchParam = useDebouncedValue(searchParam, 500);
  const [searchStatus, setSearchStatus] = useState<string[]>([]);
  const [isGoogleCalendarSettingsModalOpen, setIsGoogleCalendarSettingsModalOpen] = useState(false);
  const [isLeaderboardModalOpen, setIsLeaderboardModalOpen] = useState(false);
  const [showGoogleVacationEvents, setShowGoogleVacationEvents, isVacationVisibilityReady] =
    useLocalStorageByUser<boolean>(
      GOOGLE_CALENDAR_SHOW_VACATION_EVENTS_KEY,
      DEFAULT_GOOGLE_CALENDAR_SHOW_VACATION_EVENTS
    );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        searchRef.current?.expand();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [currentViewId, setCurrentViewId] = useState<string | null>(DEFAULT_VIEW_ID);
  const {
    views,
    isReady: isViewsReady,
    createView,
    updateView,
    deleteView,
    cloneView,
    syncViewsForGroupUpdate,
  } = useWorkboardModelsTableViews();

  const defaultNewViewName = useMemo(() => {
    if ((views ?? []).length === 0) {
      return DEFAULT_NEW_VIEW_NAME;
    }
    return `${DEFAULT_NEW_VIEW_NAME} (${views.length + 1})`;
  }, [views]);

  const [activeModelView, setActiveModelView, isActiveModelViewReady] = useLocalStorageByUser<ModelView | null>(
    ACTIVE_MODEL_VIEW_KEY,
    null
  );
  const [isFiltering, setIsFiltering] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});

  const visibleColumnIds = useMemo(() => {
    const ids: string[] = [MODELS_COLUMN_IDS.COMPANY_NAME];
    modelsColumns.forEach(col => {
      if (col.id !== MODELS_COLUMN_IDS.COMPANY_NAME && columnVisibility[col.id] !== false) {
        ids.push(col.id);
      }
    });
    return ids;
  }, [columnVisibility]);

  const [restoredActiveViewUserId, setRestoredActiveViewUserId] = useState<number | null>(null);
  const hasRestoredActiveView = restoredActiveViewUserId === currentUserId;

  // Initialize with saved active view after server views and IndexedDB hydration
  useEffect(() => {
    if (!isViewsReady || !isActiveModelViewReady || hasRestoredActiveView) {
      return;
    }

    let viewToLoad: ModelView | null = null;
    if (activeModelView) {
      const viewExists = views.find(v => v.id === activeModelView.id);
      if (viewExists) {
        viewToLoad = viewExists;
      }
    }

    viewToLoad ??= views.find(v => v.id === DEFAULT_VIEW_ID) || getDefaultModelView();

    setCurrentViewId(viewToLoad.id);
    setPreparerIds(viewToLoad.preparerIds.map(p => p.userId));
    setReviewerIds(viewToLoad.reviewerIds.map(r => r.userId));
    let statusArray: string[] = [];
    if (Array.isArray(viewToLoad.searchStatus)) {
      statusArray = viewToLoad.searchStatus;
    } else if (viewToLoad.searchStatus) {
      statusArray = [viewToLoad.searchStatus];
    }
    setSearchStatus(statusArray);
    const {
      fromDate: resolvedFromDate,
      toDate: resolvedToDate,
    } = resolveModelViewDates(viewToLoad);
    const fromDateValue = normalizeDate(resolvedFromDate);
    const toDateValue = normalizeDate(resolvedToDate);
    setFromDate(fromDateValue);
    setToDate(toDateValue);
    const filterTypeValue = normalizeModelFilterType(viewToLoad.filterType);
    setFilterType(filterTypeValue);
    setFilterTypeSelection(normalizeFilterTypeSelection(viewToLoad.filterTypeSelection, filterTypeValue));
    setColumnVisibility(viewToLoad.columnVisibility ?? {});
    setRestoredActiveViewUserId(currentUserId);
  }, [activeModelView, currentUserId, hasRestoredActiveView, isActiveModelViewReady, isViewsReady, views]);
  const [storedView, setStoredView, isStoredViewReady] = useLocalStorageByUser<ViewType>(MODELS_VIEW_KEY, 'calendar');
  const view: ViewType = storedView === 'calendar' || storedView === 'table' ? storedView : 'calendar';

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isGroupsModalOpen, setIsGroupsModalOpen] = useState(false);
  const [settingsMenuAnchor, setSettingsMenuAnchor] = useState<null | HTMLElement>(null);
  const settingsMenuOpen = Boolean(settingsMenuAnchor);
  const [colorPaletteModalOpen, setColorPaletteModalOpen] = useState(false);

  // Track color palette changes to force re-render when Done is clicked
  const [paletteVersion, setPaletteVersion] = useState(0);
  const [isPaletteChanging, setIsPaletteChanging] = useState(false);

  const handlePaletteClose = useCallback(() => {
    setIsPaletteChanging(true);
    setPaletteVersion(prev => prev + 1);
  }, []);

  // Clear the palette-changing flag after the remount has painted
  useEffect(() => {
    if (!isPaletteChanging) {
      return;
    }
    const raf = requestAnimationFrame(() => setIsPaletteChanging(false));
    return () => cancelAnimationFrame(raf);
  }, [isPaletteChanging]);
  // Column widths for resizable columns
  const [columnWidths, setColumnWidths, isColumnWidthsReady] = useLocalStorageByUser<Record<string, number>>(
    'modelsColumnWidths',
    {}
  );

  const handleColumnWidthChange = useCallback(
    (columnId: string, newWidth: number) => {
      setColumnWidths(prev => ({
        ...prev,
        [columnId]: newWidth,
      }));
    },
    [setColumnWidths]
  );

  const { data: staffUsers = [] } = useQuery<DashboardSmallUser[]>({
    queryKey: ['users-list', 'staff'],
    queryFn: async () => {
      const response = await ApiService.apiUsersStaffList();
      return response.filter(u => !u.email?.includes('+'));
    },
    staleTime: 60 * 60 * 1000,
  });

  const { zoomLevel } = useZoom();
  const { setTertiaryNavNode } = useCompanyDashboardContext();

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuViewId, setMenuViewId] = useState<string | null>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [pendingEditView, setPendingEditView] = useState<ModelView | null>(null);

  // Every ancestor up to the layout is min-height only, so `flex: 1` resolves against
  // content rather than the screen. Both views need an explicit, zoom-corrected height.
  const panelSx = useMemo(
    () => ({
      display: 'flex',
      flexDirection: 'column',
      height: calculateTableHeight(zoomLevel, 190),
      minHeight: '560px',
      overflow: 'hidden',
    }),
    [zoomLevel]
  );

  const {
    rows: models,
    isLoading,
    isLoadingMore,
    updateModelNote,
    updateModelStatus,
    updateModelDueDate,
    updateModelValuationFields,
    refetch: refetchModels,
  } = useModelsWithValuationData({
    filterType,
    filterTypeSelection,
    searchStatus,
    preparerUserIds: preparerIds,
    reviewerUserIds: reviewerIds,
    disableSorting: view === 'calendar',
    orderBy,
    order,
    sortCriteria,
    fromDate: fromDate?.toISOString().split('T')[0] ?? undefined,
    toDate: toDate?.toISOString().split('T')[0] ?? undefined,
    companyName: debouncedSearchParam.trim() || undefined,
  });

  const viewUserIds = useMemo(() => [...new Set([...preparerIds, ...reviewerIds])], [preparerIds, reviewerIds]);

  useEventSubscription<ValuationsTableValuationUpdatedPayload>(
    EventType.WORKBOARD_VALUATION_UPDATED,
    useCallback(
      ({ valuationId, updates }) => {
        if (updates.status) {
          updateModelValuationFields(valuationId, { valuation_status: updates.status });
        }
      },
      [updateModelValuationFields]
    )
  );

  const handleAutoFitColumns = useCallback(() => {
    // Calculate optimal width for Company Name based on longest company name in data
    const longestCompanyName = models.reduce((longest, row) => {
      const name = row.company_name || '';
      return name.length > longest.length ? name : longest;
    }, '');

    // Calculate width: ~8px per character + padding (cell padding + some buffer)
    const charWidth = 8;
    const padding = 32;
    const companyNameWidth = Math.max(150, Math.min(400, longestCompanyName.length * charWidth + padding));

    // Clear all custom column widths and set Company Name to calculated width
    setColumnWidths({
      company_name: companyNameWidth,
    });

  }, [setColumnWidths, models]);

  // Clear filtering state after models are filtered and rendered
  useEffect(() => {
    if (isFiltering) {
      setIsFiltering(false);
    }
  }, [models, isFiltering]);

  useEffect(() => {
    if (isRefreshing && !isLoading) {
      setIsRefreshing(false);
    }
  }, [isLoading, isRefreshing]);

  const handleNoteSaveSuccess = useCallback(
    (id: string | number, noteContent: string, createdNote?: UserNoteGet) => {
      if (createdNote?.fulfillment) {
        const fulfillmentId = createdNote.fulfillment;
        const noteId = createdNote.id;
        updateModelNote(fulfillmentId, noteContent, noteId);
      } else {
        updateModelNote(id as number, noteContent);
      }
    },
    [updateModelNote]
  );

  const { isNoteLoading, handleNoteSave: handleNoteSaveGeneric } = useNoteHandler({
    onSuccess: handleNoteSaveSuccess,
  });

  const handleNoteSave = useCallback(
    async (fulfillment: number, noteContent: string, currentNote?: string) => {
      if (!fulfillment) {
        throw new Error('Invalid fulfillment ID. Cannot save note.');
      }

      const request: CreateNoteRequest = {
        content: noteContent,
        fulfillment,
        reference_type: 'fu',
      };

      const currentModel = models.find(m => m.id === fulfillment);
      // Another user's note is never updated; the editor tooltip already warned a new note is created
      const isOtherAuthorNote = currentModel?.note?.author_id != null && currentModel.note.author_id !== currentUserId;
      // A note DB record exists if note is non-null (regardless of content being empty)
      const hasExistingNote = currentModel?.note != null && !isOtherAuthorNote;

      const existingNoteId = hasExistingNote && currentModel?.note?.id ? currentModel.note.id.toString() : undefined;

      await handleNoteSaveGeneric(fulfillment, noteContent, currentNote, request, hasExistingNote, existingNoteId);
    },
    [currentUserId, handleNoteSaveGeneric, models]
  );

  const handleStatusUpdate = useCallback((modelId: number, newStatus: number) => {
    updateModelStatus(modelId, newStatus);
  }, [updateModelStatus]);

  const { openModal, selectedModel, handleOpenModal, handleCloseModal } = useOperationsModal({ models });

  const handleEventDrop = async (modelId: number, newDueDate: string) => {
    updateModelDueDate(modelId, newDueDate);
  };

  const handleLoadView = useCallback(
    (savedView: ModelView) => {
      setIsFiltering(true);
      setPreparerIds(savedView.preparerIds.map(p => p.userId));
      setReviewerIds(savedView.reviewerIds.map(r => r.userId));
      let statusArray: string[] = [];
      if (Array.isArray(savedView.searchStatus)) {
        statusArray = savedView.searchStatus;
      } else if (savedView.searchStatus) {
        statusArray = [savedView.searchStatus];
      }
      setSearchStatus(statusArray);
      const { fromDate: resolvedFromDate, toDate: resolvedToDate } = resolveModelViewDates(savedView);
      const fromDateValue = normalizeDate(resolvedFromDate);
      const toDateValue = normalizeDate(resolvedToDate);
      setFromDate(fromDateValue);
      setToDate(toDateValue);
      const filterTypeValue = normalizeModelFilterType(savedView.filterType);
      setFilterType(filterTypeValue);
      setFilterTypeSelection(normalizeFilterTypeSelection(savedView.filterTypeSelection, filterTypeValue));
      setColumnVisibility(savedView.columnVisibility ?? {});
      setCurrentViewId(savedView.id);
      setActiveModelView(savedView);
    },
    [setActiveModelView]
  );

  const handleCreateViewFromDialog = useCallback(
    async (newView: ModelView) => {
      const created = await createView(newView);
      handleLoadView(created);
    },
    [handleLoadView, createView]
  );

  const handleEditView = useCallback(
    async (viewToEdit: ModelView) => {
      const currentView = views.find(v => v.id === viewToEdit.id);
      if (currentView && !hasViewChanged(currentView, viewToEdit)) {
        return;
      }

      await updateView(viewToEdit);
    },
    [updateView, views]
  );

  const handleEditViewConfirm = useCallback(
    (currentView: ModelView) => {
      handleEditView(currentView);
      if (currentViewId === currentView.id) {
        setColumnVisibility(currentView.columnVisibility ?? {});
      }
    },
    [currentViewId, handleEditView]
  );

  const handleDeleteView = useCallback(
    async (viewId: string) => {
      if (viewId === DEFAULT_VIEW_ID) {
        return; // Cannot delete Default view
      }
      await deleteView(viewId);
      if (currentViewId === viewId) {
        setCurrentViewId(DEFAULT_VIEW_ID);
        handleLoadView(getDefaultModelView());
      }
    },
    [currentViewId, deleteView, handleLoadView]
  );

  const handleGroupUpdated = useCallback(
    async (updatedGroup: UserGroup) => {
      const syncedViews = await syncViewsForGroupUpdate(updatedGroup);
      if (!currentViewId || !syncedViews?.length) {
        return;
      }
      const currentView = syncedViews.find(v => v.id === currentViewId);
      if (currentView) {
        handleLoadView(currentView);
      }
    },
    [currentViewId, handleLoadView, syncViewsForGroupUpdate]
  );

  const handleCloneView = useCallback(
    async (viewId: string) => {
      const source = views.find(v => v.id === viewId);
      if (!source) {
        return;
      }
      const existingNames = new Set(views.map(v => v.name));
      let cloneName = `${source.name} (Copy)`;
      let i = 2;
      while (existingNames.has(cloneName)) {
        cloneName = `${source.name} (Copy ${i})`;
        i += 1;
      }
      await cloneView(source, cloneName);
    },
    [cloneView, views]
  );

  const handleTabChange = useCallback(
    (selectedId: string) => {
      const selectedView = views.find(v => v.id === selectedId);
      if (selectedView) {
        handleLoadView(selectedView);
      }
    },
    [handleLoadView, views]
  );

  const handleAddView = useCallback(() => {
    setPendingEditView(null);
    setIsViewDialogOpen(true);
  }, []);

  const handleCloseViewDialog = useCallback(() => {
    setIsViewDialogOpen(false);
    setPendingEditView(null);
  }, []);

  const handleViewDialogSubmit = useCallback(
    (newView: ModelView) => {
      if (pendingEditView) {
        handleEditViewConfirm(newView);
      } else {
        handleCreateViewFromDialog(newView);
      }
    },
    [handleCreateViewFromDialog, handleEditViewConfirm, pendingEditView]
  );

  const handleItemMenuClick = useCallback((event: ReactMouseEvent<HTMLButtonElement>, item: TertiaryNavigationItem) => {
    event.stopPropagation();
    setMenuViewId(item.value);
    setMenuAnchor(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuAnchor(null);
    setMenuViewId(null);
  }, []);

  const handleEditViewFromMenu = useCallback(() => {
    if (!menuViewId) {
      return;
    }
    const currentView = views.find(v => v.id === menuViewId);
    if (!currentView) {
      return;
    }
    setPendingEditView(currentView);
    setMenuAnchor(null);
    setMenuViewId(null);
    setIsViewDialogOpen(true);
  }, [menuViewId, views]);

  const handleCloneViewFromMenu = useCallback(() => {
    handleMenuClose();
    if (menuViewId) {
      handleCloneView(menuViewId);
    }
  }, [handleMenuClose, menuViewId, handleCloneView]);

  const handleDeleteViewFromMenu = useCallback(() => {
    handleMenuClose();
    if (menuViewId) {
      handleDeleteView(menuViewId);
    }
  }, [handleMenuClose, menuViewId, handleDeleteView]);

  const canEditView = useCallback(
    (currentView: ModelView) => canEditTableView(currentView, currentUserId, isStaff),
    [currentUserId, isStaff]
  );

  const tertiaryItems = useMemo<TertiaryNavigationItem[]>(
    () =>
      views.map(v => ({
        label: v.name,
        value: v.id,
        showMenu: v.id !== DEFAULT_VIEW_ID && canEditView(v),
        isClosable: v.id !== DEFAULT_VIEW_ID && canEditView(v),
      })),
    [canEditView, views]
  );

  const tertiaryNavActionsNode = useMemo(
    () => (
      <Box display="flex" alignItems="center" gap={1}>
        <ViewToggle view={view} onChange={setStoredView} />
        <TertiaryNavigationIconButton
          aria-controls={settingsMenuOpen ? 'workboard-models-options-menu' : undefined}
          aria-expanded={settingsMenuOpen ? 'true' : undefined}
          aria-haspopup="true"
          ariaLabel="Open workboard options"
          icon={<SettingsOutlinedIcon fontSize="inherit" />}
          tooltipTitle="Options"
          variant="toolbar"
          onClick={e => setSettingsMenuAnchor(e.currentTarget)}
        />
        <Menu
          id="workboard-models-options-menu"
          anchorEl={settingsMenuAnchor}
          open={settingsMenuOpen}
          onClose={() => setSettingsMenuAnchor(null)}
          slotProps={{ list: { dense: true } }}>
          <MenuItem
            onClick={() => {
              setSettingsMenuAnchor(null);
              setColorPaletteModalOpen(true);
            }}>
            <ListItemIcon>
              <ColorLensOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Calendar colors</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              setSettingsMenuAnchor(null);
              handleAutoFitColumns();
            }}>
            <ListItemIcon>
              <FitScreenOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Auto-fit columns</ListItemText>
          </MenuItem>
          {isSuperuser && (
            <MenuItem
              onClick={() => {
                setSettingsMenuAnchor(null);
                setIsGroupsModalOpen(true);
              }}>
              <ListItemIcon>
                <GroupsIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Manage user groups</ListItemText>
            </MenuItem>
          )}
          <MenuItem
            onClick={() => {
              setSettingsMenuAnchor(null);
              setIsGoogleCalendarSettingsModalOpen(true);
            }}>
            <ListItemIcon>
              <CalendarMonthOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Google Calendar settings</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={() => {
              setSettingsMenuAnchor(null);
              setIsLeaderboardModalOpen(true);
            }}
            data-testid="workboard-models-leaderboard-menu-item">
            <ListItemIcon>
              <EmojiEventsOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Leaderboard</ListItemText>
          </MenuItem>
        </Menu>
        <ModelsColorSelector
          hideTrigger
          modalOpen={colorPaletteModalOpen}
          onModalOpenChange={setColorPaletteModalOpen}
          onClose={handlePaletteClose}
        />
        <ExpandableSearch
          ref={searchRef}
          value={searchParam}
          onChange={e => setSearchParam(e.target.value)}
          placeholder="Search by company name..."
        />
        <TertiaryNavigationIconButton
          ariaLabel="Refresh data"
          disabled={isRefreshing}
          icon={isRefreshing ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon fontSize="inherit" />}
          tooltipTitle="Refresh data"
          variant="toolbar"
          onClick={() => {
            setIsRefreshing(true);
            refetchModels();
          }}
        />
        <Box
          sx={{
            alignItems: 'center',
            backgroundColor: 'action.hover',
            borderRadius: '8px',
            display: 'inline-flex',
            px: 1.5,
            py: 0.5,
            whiteSpace: 'nowrap',
          }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {(() => {
              if (isFiltering || isLoading || isRefreshing) {
                return 'Loading models...';
              }
              if (isLoadingMore) {
                return `Loading models: ${models.length.toLocaleString()} loaded...`;
              }
              return `Total models: ${models.length.toLocaleString()}`;
            })()}
          </Typography>
        </Box>
      </Box>
    ),
    [
      view,
      settingsMenuOpen,
      settingsMenuAnchor,
      isSuperuser,
      colorPaletteModalOpen,
      handlePaletteClose,
      searchParam,
      isRefreshing,
      refetchModels,
      isFiltering,
      isLoading,
      isLoadingMore,
      models.length,
      handleAutoFitColumns,
      setStoredView,
    ]
  );

  const arePreferencesReady =
    isSortReady &&
    isVacationVisibilityReady &&
    isViewsReady &&
    isActiveModelViewReady &&
    hasRestoredActiveView &&
    isStoredViewReady &&
    isColumnWidthsReady &&
    isPaletteReady;

  const tertiaryNavNode = useMemo(
    () =>
      arePreferencesReady ? (
        <TertiaryNavigation
          items={tertiaryItems}
          leftActions={<TertiaryNavigationAddButton onClick={handleAddView} />}
          actions={tertiaryNavActionsNode}
          value={currentViewId ?? DEFAULT_VIEW_ID}
          onChange={handleTabChange}
          onItemClose={(_, item) => handleDeleteView(item.value)}
          onItemMenuClick={handleItemMenuClick}
          zoomLevel={zoomLevel}
        />
      ) : null,
    [
      arePreferencesReady,
      tertiaryItems,
      handleAddView,
      tertiaryNavActionsNode,
      currentViewId,
      handleTabChange,
      handleDeleteView,
      handleItemMenuClick,
      zoomLevel,
    ]
  );

  useEffect(() => {
    setTertiaryNavNode?.(tertiaryNavNode);
    return () => {
      setTertiaryNavNode?.(null);
    };
  }, [setTertiaryNavNode, tertiaryNavNode]);

  if (!arePreferencesReady) {
    return (
      <GenericWorkboard>
        <LoadingSection />
      </GenericWorkboard>
    );
  }

  return (
    <GenericWorkboard>
      <Box sx={{ overflow: 'hidden' }}>
        {view === 'calendar' ? (
          <Box sx={panelSx}>
            <ModelsCalendarWithToolbar
              key={paletteVersion}
              viewUserIds={viewUserIds}
              models={models}
              isLoading={isLoading || isFiltering || isRefreshing || isPaletteChanging}
              onOpenPreview={handleOpenModal}
              onEventDrop={handleEventDrop}
              showGoogleVacationEvents={showGoogleVacationEvents ?? DEFAULT_GOOGLE_CALENDAR_SHOW_VACATION_EVENTS}
            />
          </Box>
        ) : (
          <Box sx={panelSx}>
            <WorkboardModelsSpreadsheet
              key={paletteVersion}
              models={models}
              visibleColumnIds={visibleColumnIds}
              isLoading={isLoading || isFiltering || isRefreshing}
              isNoteLoading={isNoteLoading}
              columnWidths={columnWidths}
              sortCriteria={sortCriteria}
              onRequestSort={handleRequestSort}
              onColumnResize={handleColumnWidthChange}
              onRowClick={handleOpenModal}
              onStatusUpdate={handleStatusUpdate}
              onNoteSave={handleNoteSave}
            />
          </Box>
        )}
      </Box>
      <OperationsModal open={openModal} onClose={handleCloseModal} data={selectedModel} isFromModels />
      {isSuperuser && (
        <UserGroupsModal
          open={isGroupsModalOpen}
          onClose={() => setIsGroupsModalOpen(false)}
          onGroupUpdated={handleGroupUpdated}
        />
      )}
      <GoogleCalendarSettingsModal
        open={isGoogleCalendarSettingsModalOpen}
        showVacationEvents={showGoogleVacationEvents ?? DEFAULT_GOOGLE_CALENDAR_SHOW_VACATION_EVENTS}
        onClose={() => setIsGoogleCalendarSettingsModalOpen(false)}
        onShowVacationEventsChange={setShowGoogleVacationEvents}
      />
      <FulfillmentModelsLeaderboardModal
        open={isLeaderboardModalOpen}
        onClose={() => setIsLeaderboardModalOpen(false)}
      />
      <PrimaryNavigationMenu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor && menuViewId)}
        onClose={handleMenuClose}
        menuMinWidth={0}
        menuWidth="content"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <PrimaryNavigationMenuItem icon={<EditIcon fontSize="inherit" />} onClick={handleEditViewFromMenu}>
          Edit View
        </PrimaryNavigationMenuItem>
        <PrimaryNavigationMenuItem icon={<ContentCopyIcon fontSize="inherit" />} onClick={handleCloneViewFromMenu}>
          Clone
        </PrimaryNavigationMenuItem>
        <Divider />
        <PrimaryNavigationMenuItem
          icon={<DeleteIcon fontSize="inherit" />}
          onClick={handleDeleteViewFromMenu}
          tone="danger">
          Delete
        </PrimaryNavigationMenuItem>
      </PrimaryNavigationMenu>
      <ModelsNewViewDialog
        open={isViewDialogOpen}
        onClose={handleCloseViewDialog}
        onCreateView={handleViewDialogSubmit}
        currentColumnVisibility={columnVisibility}
        staffUsers={staffUsers}
        defaultName={defaultNewViewName}
        initialView={pendingEditView ?? undefined}
      />
    </GenericWorkboard>
  );
};

export default WorkboardModelsTable;
